import React from 'react';
import { Box, TextField, Switch, Typography, Radio, Divider } from '@material-ui/core';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { IconPicker } from '../../../../../common/icons/IconPicker';

export type ApplicationMode = 'auto' | 'tasks_auto' | 'tasks_manual' | 'approve_tasks_manual';

export interface ValueType {
  name: string;
  icon: any;
  description: any;
  canApply: boolean;
  applicationMode: ApplicationMode;
  allowReapply: boolean;
}

export const APPLICATION_MODE_OPTIONS: { value: ApplicationMode; label: string; helper: string }[] = [
  {
    value: 'auto',
    label: 'Auto Approval',
    helper: 'Volunteer is granted the role immediately on application.',
  },
  {
    value: 'tasks_auto',
    label: 'Complete Tasks',
    helper: 'Volunteer must complete the role’s tasks; approval happens automatically.',
  },
  {
    value: 'tasks_manual',
    label: 'Tasks & Review',
    helper: 'Volunteer completes tasks, then a manager reviews and approves.',
  },
  {
    value: 'approve_tasks_manual',
    label: 'Review, Tasks & Approval',
    helper: 'Manager screens the application first, then volunteer completes tasks for a final review.',
  },
];

export interface Props {
  value: ValueType;
  onChange: (value: ValueType) => void;
}

export function RoleForm({ value, onChange }: Props) {
  return (
    <>
      <Box display='flex' style={{ gap: '16px', alignItems: 'center' }}>
        <IconPicker value={value.icon} onChange={(key) => onChange({ ...value, icon: key })} placeholder='Badge' />

        <TextField
          fullWidth
          variant='outlined'
          label='Role Title'
          name='name'
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
        />
      </Box>
      <Box marginTop='1.25rem'>
        <CampfireRichEditor
          placeholder='Add a description for this role (eg. duties, expectations, etc.)…'
          editorState={value.description}
          setEditorState={(eState) => {
            onChange({ ...value, description: eState });
          }}
        />
      </Box>
      <Box marginTop='1.25rem'>
        <Box display='flex' flex='1 1 auto' marginTop='.5em'>
          <Switch
            name='can-apply-switch'
            color='primary'
            checked={value.canApply}
            onChange={() => onChange({ ...value, canApply: !value.canApply })}
            inputProps={{ 'aria-label': 'can apply switch' }}
          />
          <Box ml={1}>
            <Typography style={{ fontWeight: 500, fontSize: '14px', color: '#333' }}>Users Can Apply</Typography>
            <Typography style={{ fontSize: '13px', color: '#808080' }}>
              Volunteers can apply to this role on the application form and (when enabled) the Roles tab.
            </Typography>
          </Box>
        </Box>
      </Box>
      {value.canApply && (
        <Box marginTop='2rem'>
          <Divider style={{ marginBottom: '1.25rem' }} />
          <Typography style={{ fontWeight: 600, fontSize: '16px', color: '#333', marginBottom: '4px' }}>
            Application Settings
          </Typography>
          <Typography style={{ fontSize: '13px', color: '#808080', marginBottom: '1rem' }}>
            How will volunteer applications for this role be handled?
          </Typography>

          <Typography style={{ fontWeight: 500, fontSize: '14px', color: '#333', marginBottom: '4px' }}>
            Approval Method
          </Typography>
          <Typography style={{ fontSize: '13px', color: '#808080', marginBottom: '12px' }}>
            Choose how volunteers move from applying to being granted this role.
          </Typography>

          <Box display='flex' flexDirection='column' style={{ gap: '8px' }}>
            {APPLICATION_MODE_OPTIONS.map((option) => {
              const selected = value.applicationMode === option.value;
              return (
                <Box
                  key={option.value}
                  onClick={() => onChange({ ...value, applicationMode: option.value })}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '12px',
                    border: `1px solid ${selected ? '#0288d1' : '#e0e0e0'}`,
                    borderRadius: '4px',
                    backgroundColor: selected ? '#f1f8fd' : '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <Radio
                    checked={selected}
                    color='primary'
                    style={{ padding: 0, marginRight: '12px', marginTop: '2px' }}
                  />
                  <Box>
                    <Typography style={{ fontWeight: 500, fontSize: '14px', color: '#333' }}>{option.label}</Typography>
                    <Typography style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
                      {option.helper}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box marginTop='1.5rem'>
            <Box display='flex' flex='1 1 auto'>
              <Switch
                name='allow-reapply-switch'
                color='primary'
                checked={value.allowReapply}
                onChange={() => onChange({ ...value, allowReapply: !value.allowReapply })}
                inputProps={{ 'aria-label': 'allow reapply switch' }}
              />
              <Box ml={1}>
                <Typography style={{ fontWeight: 500, fontSize: '14px', color: '#333' }}>
                  Allow re-applications after rejection
                </Typography>
                <Typography style={{ fontSize: '13px', color: '#808080' }}>
                  Volunteers whose applications were rejected can apply again.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
